import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouteContext, useRouter, useRouterState } from "@tanstack/react-router";
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { allows } from "@manos/core";
import { api, mediaQuery, worksQuery } from "../lib/api";
import { SCREEN_NAMES, type Screen, type TourStep, screenOf, tourFor } from "../lib/tour";

/*
 * The guided tour (lib/tour.ts has the steps). A spotlight on the real control and a card beside it;
 * steps move between screens on their own, and a step whose control isn't on screen is skipped. The
 * page underneath can't be clicked while it runs, so the tour never changes anything. Progress is
 * saved to the person's account (offered yet; which tours they've finished).
 */
type Tours = { offered?: boolean; done?: string[] };
const toursQuery = { queryKey: ["tours"], queryFn: () => api<{ tours: Tours }>("/studio/me/tours").then((r) => r.tours) };

/** Start a tour from anywhere (⌘K sits outside the tour's context). */
export const startTour = (which: Screen | "full") => window.dispatchEvent(new CustomEvent("manos:tour", { detail: which }));

const TourContext = createContext<{ start: (which: Screen | "full") => void; running: boolean } | null>(null);
export const useTour = () => useContext(TourContext);

const PHONE = "(max-width: 760px)";
const visible = (el: Element) => {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== "hidden";
};
/** The first selector (in order of preference) with a visible match. */
const find = (target: string | string[]) => {
  for (const selector of Array.isArray(target) ? target : [target]) {
    const el = [...document.querySelectorAll(selector)].find(visible);
    if (el) return el;
  }
  return null;
};
const exists = (target: string | string[]) => (Array.isArray(target) ? target : [target]).some((s) => document.querySelector(s));

export function TourProvider({ children }: { children: React.ReactNode }) {
  const { user } = useRouteContext({ from: "/_app" });
  const qc = useQueryClient();
  const router = useRouter();
  const tours = useQuery(toursQuery);
  const [run, setRun] = useState<{ which: Screen | "full"; steps: TourStep[]; i: number; dir: 1 | -1 } | null>(null);
  const save = useCallback(
    (patch: Tours) => void api<{ tours: Tours }>("/studio/me/tours", { method: "PUT", json: patch }).then((r) => qc.setQueryData(toursQuery.queryKey, r.tours)),
    [qc],
  );
  const start = useCallback(
    (which: Screen | "full") => {
      const steps = tourFor(which, user.role);
      if (steps.length) setRun({ which, steps, i: 0, dir: 1 });
    },
    [user.role],
  );
  const end = useCallback(
    (finished: boolean) => {
      if (run && finished) save({ done: [run.which] });
      setRun(null);
    },
    [run, save],
  );

  useEffect(() => {
    const onStart = (e: Event) => start((e as CustomEvent<Screen | "full">).detail);
    window.addEventListener("manos:tour", onStart);
    return () => window.removeEventListener("manos:tour", onStart);
  }, [start]);

  const offer = tours.data && !tours.data.offered && !run;
  return (
    <TourContext.Provider value={{ start, running: Boolean(run) }}>
      {children}
      {offer && (
        <Welcome
          onStart={() => {
            save({ offered: true });
            start("full");
          }}
          onLater={() => save({ offered: true })}
        />
      )}
      {run && (
        <TourRunner
          key={`${run.which}`}
          steps={run.steps}
          index={run.i}
          dir={run.dir}
          router={router}
          qc={qc}
          onMove={(i, dir) => (i >= run.steps.length ? end(true) : i < 0 ? undefined : setRun({ ...run, i, dir }))}
          onEnd={() => end(run.i >= run.steps.length - 1)}
          onSkip={(i, dir) => {
            // A step that isn't on screen for this person leaves the tour, so the count stays true.
            const steps = run.steps.filter((_, j) => j !== i);
            const next = dir === 1 ? i : i - 1;
            if (next >= steps.length) return end(true);
            setRun({ ...run, steps, i: Math.max(0, next), dir: next < 0 ? 1 : dir });
          }}
        />
      )}
    </TourContext.Provider>
  );
}

function Welcome({ onStart, onLater }: { onStart: () => void; onLater: () => void }) {
  return (
    <div className="tour-welcome" role="dialog" aria-label="Welcome to Manos Studio">
      <b>Welcome to Manos Studio</b>
      <p className="muted small">A three-minute look around: where everything is, and how the team works together here.</p>
      <div className="row">
        <button type="button" className="btn sm primary" onClick={onStart}>
          Take the tour
        </button>
        <button type="button" className="btn sm ghost" onClick={onLater}>
          Later
        </button>
      </div>
      <small className="muted">You can start it any time from your name, or ⌘K.</small>
    </div>
  );
}

function TourRunner({
  steps,
  index,
  dir,
  router,
  qc,
  onMove,
  onEnd,
  onSkip,
}: {
  steps: TourStep[];
  index: number;
  dir: 1 | -1;
  router: ReturnType<typeof useRouter>;
  qc: ReturnType<typeof useQueryClient>;
  onMove: (i: number, dir: 1 | -1) => void;
  onEnd: () => void;
  onSkip: (index: number, dir: 1 | -1) => void;
}) {
  const step = steps[index]!;
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [ready, setReady] = useState(false);
  const target = useRef<Element | null>(null);
  const card = useRef<HTMLDivElement>(null);
  const path = useRouterState({ select: (s) => s.location.pathname });

  // Go to the step's screen (resolving "the first work you can open"), wait for its control, or skip it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per step
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setRect(null);
    target.current = null;
    (async () => {
      let to: string | null = null;
      if (step.route === "first-work") {
        if (!/^\/works\/[^/]+/.test(path)) {
          const works = await qc.fetchQuery(worksQuery).catch(() => []);
          const w = works.find((x) => allows(x.access, "view"));
          to = w ? `/works/${w.id}` : null;
          if (!w) return !cancelled && onSkip(index, dir);
        }
      } else if (step.route === "first-media") {
        const media = await qc.fetchQuery(mediaQuery).catch(() => []);
        const m = media.find((x) => x.status === "ready" && allows(x.access, "edit")) ?? media.find((x) => x.status === "ready");
        if (!m) return !cancelled && onSkip(index, dir);
        if (!window.location.search.includes(m.id)) to = `/media?id=${m.id}`;
      } else if (step.route && step.route !== path) to = step.route;
      if (to) {
        router.history.push(to);
        // Wait for the new screen itself, so the previous screen's controls are never pointed at.
        const want = to.split("?")[0];
        for (let t = 0; t < 30 && !cancelled && window.location.pathname !== want; t++) await new Promise((r) => setTimeout(r, 50));
        await new Promise((r) => requestAnimationFrame(() => r(null)));
      }
      if (!step.target) return !cancelled && setReady(true);
      // A new screen gets time to load; on the same screen, a control that's there but hidden (the
      // phone's folded sidebar) or missing for this person is skipped at once.
      const tries = to ? 40 : 5;
      for (let t = 0; t < tries && !cancelled; t++) {
        if (!to && t > 0 && exists(step.target)) break;
        const el = find(step.target);
        if (el) {
          target.current = el;
          const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
          if (window.matchMedia(PHONE).matches) {
            // Phones: bring it near the top, under the Studio bar, leaving the lower screen for the card.
            // An instant jump, so the card is placed against where the control really ends up.
            const top = el.getBoundingClientRect().top + window.scrollY - 84;
            window.scrollTo({ top: Math.max(0, top), behavior: "auto" });
          } else el.scrollIntoView({ block: "center", behavior: smooth ? "smooth" : "auto" });
          await new Promise((r) => setTimeout(r, 250));
          if (!cancelled) {
            setRect(el.getBoundingClientRect());
            setReady(true);
          }
          return;
        }
        await new Promise((r) => setTimeout(r, 100));
      }
      if (!cancelled) onSkip(index, dir); // not on screen for this person: skip it
    })();
    return () => {
      cancelled = true;
    };
  }, [step.id]);

  // Keep the spotlight on the control as the page moves.
  useEffect(() => {
    if (!ready || !target.current) return;
    const measure = () => target.current && setRect(target.current.getBoundingClientRect());
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    const t = setInterval(measure, 400);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      clearInterval(t);
    };
  }, [ready]);

  // Keys belong to the tour while it runs: → Enter next, ← back, Esc end.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onEnd();
      else if (e.key === "ArrowRight" || e.key === "Enter") onMove(index + 1, 1);
      else if (e.key === "ArrowLeft" && index > 0) onMove(index - 1, -1);
      else return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [index, onMove, onEnd]);

  // Each step starts not ready, so this runs as every step's card appears.
  useLayoutEffect(() => {
    if (ready) card.current?.querySelector<HTMLButtonElement>("[data-next]")?.focus();
  }, [ready]);

  const phone = window.matchMedia(PHONE).matches;
  const pad = 6;
  const style = cardPosition(rect, phone);
  // Phones: a control taller than the room above the card is lit only down to it (offsetTop ignores the card's entrance animation).
  const [cardTop, setCardTop] = useState<number | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measured whenever the lit control moves
  useLayoutEffect(() => {
    setCardTop(ready && phone && card.current ? card.current.offsetTop : null);
  }, [ready, phone, rect]);
  const lit =
    rect && phone && cardTop !== null && rect.top < cardTop && rect.bottom > cardTop - 12 ? { ...rect.toJSON(), height: cardTop - 12 - pad - rect.top } : rect;
  return (
    <div className="tour" aria-live="polite">
      {/* The dimmed page: catches clicks so nothing underneath changes during the tour. */}
      <div className={`tour-shade ${rect ? "has-hole" : ""}`} onClick={(e) => e.stopPropagation()} aria-hidden="true" />
      {lit && (
        <div
          className="tour-hole"
          style={{ left: lit.left - pad, top: lit.top - pad, width: lit.width + pad * 2, height: lit.height + pad * 2 }}
          aria-hidden="true"
        />
      )}
      {ready && (
        <div className={`tour-card ${phone ? "phone" : ""}`} ref={card} style={style} role="dialog" aria-modal="true" aria-labelledby="tour-title">
          <small className="muted tour-count">
            {index + 1} of {steps.length}
          </small>
          <h2 id="tour-title">{step.title}</h2>
          <p>{step.body}</p>
          <div className="tour-actions">
            <button type="button" className="link muted" onClick={onEnd}>
              {index === steps.length - 1 ? "Close" : "End tour"}
            </button>
            <span className="row">
              {index > 0 && (
                <button type="button" className="btn sm" onClick={() => onMove(index - 1, -1)}>
                  Back
                </button>
              )}
              <button type="button" className="btn sm primary" data-next onClick={() => onMove(index + 1, 1)}>
                {index === steps.length - 1 ? "Done" : "Next"}
              </button>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Beside the control: below it if there's room, else above, else centred. Phones: along the bottom,
 * or along the top when the control reaches into the bottom of the screen, so it's never covered.
 */
function cardPosition(rect: DOMRect | null, phone: boolean): React.CSSProperties {
  if (phone) {
    const vh = window.innerHeight;
    // A tall control (a whole list) keeps the card at the bottom and is lit only down to it.
    return rect && rect.height < vh * 0.4 && rect.bottom > vh * 0.58 && rect.top > 120
      ? { top: "calc(72px + env(safe-area-inset-top, 0px))", bottom: "auto" }
      : {};
  }
  const W = 340;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect) return { left: (vw - W) / 2, top: Math.max(24, vh / 2 - 120), width: W };
  const left = Math.min(Math.max(16, rect.left), vw - W - 16);
  if (vh - rect.bottom > 220) return { left, top: rect.bottom + 14, width: W };
  if (rect.top > 220) return { left, bottom: vh - rect.top + 14, width: W };
  return { left: Math.min(Math.max(16, rect.right + 16), vw - W - 16), top: Math.max(16, Math.min(rect.top, vh - 260)), width: W };
}

/** "?" in a page's top bar: replays that screen's tour. */
export function TourHelp() {
  const tour = useTour();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { user } = useRouteContext({ from: "/_app" });
  const screen = screenOf(path);
  // Only where this person has something to be shown (their role and the screen).
  if (!tour || !screen || !tourFor(screen, user.role).length) return null;
  return (
    <button
      type="button"
      className="bell tour-help"
      onClick={() => tour.start(screen)}
      aria-label={`Tour of ${SCREEN_NAMES[screen]}`}
      title={`Tour of ${SCREEN_NAMES[screen]}`}
    >
      ?
    </button>
  );
}
